import {
  STAFF_CATALOG_VERIFICATION_STALE_DAYS,
  type StaffCatalogItemDetail,
  type StaffCatalogPublishabilityIssue,
} from "@lafayette/schemas";

import { type SectionId, verificationFreshness } from "./labels";

// What a staff member should see before a publish: the server's issues (BLOCKING stops a publish,
// WARNING deserves a look) and the warnings the console derives from the item itself (stale
// verification, a fixed price that is not payment-grade). The derived ones never block: only the server
// decides what blocks.

export type ReadinessIssues = Readonly<{
  blocking: readonly StaffCatalogPublishabilityIssue[];
  warnings: readonly StaffCatalogPublishabilityIssue[];
}>;

export function splitIssues(issues: readonly StaffCatalogPublishabilityIssue[]): ReadinessIssues {
  return {
    blocking: issues.filter((issue) => issue.severity === "BLOCKING"),
    warnings: issues.filter((issue) => issue.severity === "WARNING"),
  };
}

export type DerivedWarning = Readonly<{ id: string; message: string; section: SectionId }>;

export function derivedWarnings(
  detail: StaffCatalogItemDetail,
  now: Date,
): readonly DerivedWarning[] {
  const warnings: DerivedWarning[] = [];
  const { item } = detail;
  const freshness = verificationFreshness(item.lastVerifiedAt, now);
  if (freshness === "never") {
    warnings.push({
      id: "never-verified",
      message: "This item has never been verified.",
      section: "verification",
    });
  } else if (freshness === "stale") {
    warnings.push({
      id: "stale-verification",
      message: `This item was last verified over ${STAFF_CATALOG_VERIFICATION_STALE_DAYS} days ago. Check it again.`,
      section: "verification",
    });
  }
  if (item.pricingModel === "FIXED" && !item.priceAuthoritative) {
    warnings.push({
      id: "price-not-payment-grade",
      message:
        "The fixed price is not authorized for payment. Customers will see it but cannot pay it.",
      section: "pricing",
    });
  }
  if (item.priceAuthoritative && item.fulfilmentMode !== "HUMAN_CONCIERGE") {
    warnings.push({
      id: "payment-grade-fulfilment",
      message: "A payment-grade price needs the human concierge fulfilment mode.",
      section: "details",
    });
  }
  return warnings;
}

/**
 * Why a FIXED price is not payment-grade, as far as the staff detail shows. The API decides the class;
 * this only explains it, so a staff member knows what to change. It repeats the checks of the domain's
 * `resolveEligibleFixedPrice` that the detail carries. The currency allow-list is not in the detail, so
 * it is named as a possible cause and never as a fact.
 */
export function paymentBlockers(detail: StaffCatalogItemDetail): readonly string[] {
  const { item } = detail;
  if (item.pricingModel !== "FIXED" || detail.priceClass === "PAYMENT_GRADE") return [];
  const reasons: string[] = [];
  if (!item.priceAuthoritative) reasons.push("The price is not authorized for payment yet.");
  if (item.priceValidUntil === null && !item.priceNoExpiry) {
    reasons.push("The price needs an end date or “The price does not expire”.");
  }
  if (item.status !== "PUBLISHED") reasons.push("The item is not published.");
  if (item.priceUnit !== "PER_BOOKING") {
    reasons.push("Only a “Per booking” price can be paid for now.");
  }
  if (item.priceAuthoritative && reasons.length === 0) {
    reasons.push(
      "The price has expired, is not valid yet, or its currency is not accepted for payment.",
    );
  }
  return reasons;
}
